import { supabase } from '../lib/supabase';

/**
 * Fetch artist dashboard metrics
 */
export const fetchArtistMetrics = async (userId) => {
  try {
    // Fetch campaigns for the artist
    const { data: campaigns, error: campaignsError } = await supabase?.from('campaigns')?.select('*')?.eq('artist_id', userId);

    if (campaignsError) throw campaignsError;

    // Calculate metrics
    const totalCampaigns = campaigns?.length || 0;
    const activeCampaigns = campaigns?.filter((c) => c?.status === 'active')?.length || 0;
    const totalBudget = campaigns?.reduce((sum, c) => sum + parseFloat(c?.budget || 0), 0);

    // Fetch campaign influencers for reach calculation
    const campaignIds = campaigns?.map((c) => c?.id) || [];
    let totalReach = 0;
    let avgEngagement = 0;

    if (campaignIds?.length > 0) {
      const { data: campaignInfluencers } = await supabase
        ?.from('campaign_influencers')
        ?.select('influencer_id')
        ?.in('campaign_id', campaignIds);

      if (campaignInfluencers && campaignInfluencers?.length > 0) {
        totalReach = campaignInfluencers?.length * 10000; // Estimate reach
        avgEngagement = 3.5; // Default engagement rate
      }
    }

    return {
      totalReach: formatNumber(totalReach),
      engagementRate: avgEngagement?.toFixed(1),
      activeCampaigns: activeCampaigns?.toString(),
      totalRevenue: formatCurrency(totalBudget),
      totalCampaigns,
      campaigns: campaigns || []
    };
  } catch (error) {
    console.error('Error fetching artist metrics:', error);
    return null;
  }
};

/**
 * Fetch artist campaigns with details
 */
export const fetchArtistCampaigns = async (userId) => {
  try {
    const { data: campaigns, error } = await supabase?.from('campaigns')?.select(`
        *,
        campaign_influencers(
          id,
          influencer_id,
          allocated_budget
        )
      `)?.eq('artist_id', userId)?.order('created_at', { ascending: false })?.limit(10);

    if (error) throw error;

    // Transform campaigns to match UI format
    return campaigns?.map((campaign) => {
      const influencerCount = campaign?.campaign_influencers?.length || 0;
      let totalReach = influencerCount * 10000; // Estimate reach
      let avgEngagement = influencerCount > 0 ? 3.5 : 0;

      // Calculate progress based on status
      let progress = 0;
      if (campaign?.status === 'draft') progress = 10;else
      if (campaign?.status === 'pending') progress = 25;else
      if (campaign?.status === 'active') progress = 60;else
      if (campaign?.status === 'completed') progress = 100;

      return {
        id: campaign?.id,
        title: campaign?.name,
        genre: campaign?.description?.substring(0, 50) || 'Müzik Kampanyası',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_13c280839-1766220512991.png",
        imageAlt: 'Campaign promotional image',
        status: campaign?.status,
        influencerCount,
        budget: formatCurrency(parseFloat(campaign?.budget || 0)),
        reach: formatNumber(totalReach),
        engagement: `%${avgEngagement?.toFixed(1)}`,
        progress
      };
    }) || [];
  } catch (error) {
    console.error('Error fetching artist campaigns:', error);
    return [];
  }
};

/**
 * Fetch recommended influencers for artist
 */
export const fetchRecommendedInfluencers = async (limit = 4) => {
  try {
    const { data: influencers, error } = await supabase
      ?.from('user_profiles')
      ?.select('*')
      ?.eq('role', 'influencer')
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    if (error) throw error;

    return influencers?.map((inf) => {
      return {
        id: inf?.id,
        name: inf?.full_name || inf?.email?.split('@')?.[0] || 'Influencer',
        avatar: inf?.avatar_url || null,
        platforms: [],
        startingPrice: 'İletişime Geç'
      };
    }) || [];
  } catch (error) {
    console.error('Error fetching recommended influencers:', error);
    return [];
  }
};

/**
 * Fetch influencer dashboard earnings
 */
export const fetchInfluencerEarnings = async (userId) => {
  try {
    // Get influencer profile from user_profiles
    const { data: influencer } = await supabase
      ?.from('user_profiles')
      ?.select('id')
      ?.eq('id', userId)
      ?.eq('role', 'influencer')
      ?.single();

    if (!influencer) {
      return {
        monthly: 0,
        pending: 0,
        pendingCount: 0,
        total: 0,
        commission: 0,
        net: 0
      };
    }

    // Fetch campaign influencer relationships
    const { data: campaignInfluencers, error } = await supabase?.from('campaign_influencers')?.select(`
        allocated_budget,
        proposal_accepted,
        campaigns(
          status
        )
      `)?.eq('influencer_id', influencer?.id);

    if (error) throw error;

    // Calculate earnings
    const acceptedDeals = campaignInfluencers?.filter((ci) => ci?.proposal_accepted) || [];
    const completedDeals = acceptedDeals?.filter((ci) => ci?.campaigns?.status === 'completed');
    const activeDeals = acceptedDeals?.filter((ci) => ci?.campaigns?.status === 'active');

    const totalEarnings = completedDeals?.reduce((sum, ci) => sum + parseFloat(ci?.allocated_budget || 0), 0);
    const pendingEarnings = activeDeals?.reduce((sum, ci) => sum + parseFloat(ci?.allocated_budget || 0), 0);
    const monthlyEarnings = totalEarnings; // Simplified - could filter by date

    const commission = totalEarnings * 0.15;
    const netEarnings = totalEarnings - commission;

    return {
      monthly: monthlyEarnings,
      pending: pendingEarnings,
      pendingCount: activeDeals?.length,
      total: totalEarnings,
      commission,
      net: netEarnings
    };
  } catch (error) {
    console.error('Error fetching influencer earnings:', error);
    return {
      monthly: 0,
      pending: 0,
      pendingCount: 0,
      total: 0,
      commission: 0,
      net: 0
    };
  }
};

/**
 * Fetch influencer active campaigns
 */
export const fetchInfluencerCampaigns = async (userId) => {
  try {
    // Use userId directly since user_profiles.id = auth.users.id
    const { data: campaignInfluencers, error } = await supabase?.from('campaign_influencers')?.select(`
        id,
        allocated_budget,
        campaigns(
          id,
          name,
          end_date,
          status,
          artist_id,
          user_profiles(
            full_name,
            avatar_url
          )
        ),
        deliverables(
          deliverable_type
        )
      `)?.eq('influencer_id', userId)?.eq('proposal_accepted', true)?.in('campaigns.status', ['active', 'pending'])?.order('created_at', { ascending: false })?.limit(5);

    if (error) throw error;

    return campaignInfluencers?.map((ci) => {
      const campaign = ci?.campaigns;
      const deliverables = ci?.deliverables || [];
      const completedCount = Math.floor(deliverables?.length * 0.6); // Simulated

      const milestones = deliverables?.slice(0, 3)?.map((d, idx) => ({
        name: d?.deliverable_type,
        completed: idx < completedCount
      }));

      let progress = deliverables?.length > 0 ?
      Math.round(completedCount / deliverables?.length * 100) :
      50;

      return {
        id: campaign?.id,
        title: campaign?.name,
        artistName: campaign?.user_profiles?.full_name || 'Artist',
        artistImage: campaign?.user_profiles?.avatar_url || 'https://via.placeholder.com/150',
        artistImageAlt: `${campaign?.user_profiles?.full_name} profile picture`,
        payment: parseFloat(ci?.allocated_budget || 0),
        deadline: campaign?.end_date,
        status: campaign?.status === 'active' ? 'in-progress' : 'review',
        progress,
        milestones
      };
    }) || [];
  } catch (error) {
    console.error('Error fetching influencer campaigns:', error);
    return [];
  }
};

/**
 * Fetch influencer social media stats
 */
export const fetchInfluencerSocialStats = async (userId) => {
  try {
    const { data: socialLinks, error } = await supabase?.from('social_links')?.select('*')?.eq('user_id', userId)?.single();

    if (error || !socialLinks) {
      return [];
    }

    const platforms = [];

    if (socialLinks?.instagram_followers) {
      platforms?.push({
        name: 'Instagram',
        username: 'user_instagram',
        followers: socialLinks?.instagram_followers,
        engagement: 7.2, // Could be calculated from actual data
        reach: Math.round(socialLinks?.instagram_followers * 2.5),
        verified: socialLinks?.instagram_verified,
        lastUpdated: new Date(socialLinks?.updated_at)?.toLocaleDateString('tr-TR')
      });
    }

    if (socialLinks?.youtube_followers) {
      platforms?.push({
        name: 'YouTube',
        username: 'user_youtube',
        followers: socialLinks?.youtube_followers,
        engagement: 5.8,
        reach: Math.round(socialLinks?.youtube_followers * 3),
        verified: socialLinks?.youtube_verified,
        lastUpdated: new Date(socialLinks?.updated_at)?.toLocaleDateString('tr-TR')
      });
    }

    if (socialLinks?.tiktok_followers) {
      platforms?.push({
        name: 'TikTok',
        username: 'user_tiktok',
        followers: socialLinks?.tiktok_followers,
        engagement: 12.4,
        reach: Math.round(socialLinks?.tiktok_followers * 4),
        verified: socialLinks?.tiktok_verified,
        lastUpdated: new Date(socialLinks?.updated_at)?.toLocaleDateString('tr-TR')
      });
    }

    if (socialLinks?.twitter_followers) {
      platforms?.push({
        name: 'Twitter',
        username: 'user_twitter',
        followers: socialLinks?.twitter_followers,
        engagement: 4.5,
        reach: Math.round(socialLinks?.twitter_followers * 2),
        verified: socialLinks?.twitter_verified,
        lastUpdated: new Date(socialLinks?.updated_at)?.toLocaleDateString('tr-TR')
      });
    }

    return platforms;
  } catch (error) {
    console.error('Error fetching social stats:', error);
    return [];
  }
};

/**
 * Fetch artist financial summary
 */
export const fetchArtistFinancialSummary = async (userId) => {
  try {
    const { data: campaigns, error } = await supabase?.from('campaigns')?.select(`
        budget,
        status,
        campaign_influencers(
          allocated_budget
        )
      `)?.eq('artist_id', userId);

    if (error) throw error;

    const totalSpent = campaigns?.reduce((sum, c) => {
      const spent = c?.campaign_influencers?.reduce((s, ci) => s + parseFloat(ci?.allocated_budget || 0), 0);
      return sum + spent;
    }, 0) || 0;

    const activeCampaigns = campaigns?.filter((c) => c?.status === 'active')?.length || 0;
    const commission = totalSpent * 0.15;

    const pendingPayments = campaigns?.
    filter((c) => c?.status === 'active')?.
    reduce((sum, c) => {
      const pending = c?.campaign_influencers?.reduce((s, ci) => s + parseFloat(ci?.allocated_budget || 0), 0);
      return sum + pending;
    }, 0) || 0;

    const completedPayments = totalSpent - pendingPayments;

    return {
      totalSpent: formatCurrency(totalSpent),
      activeCampaigns: activeCampaigns?.toString(),
      commission: formatCurrency(commission),
      pendingPayments: formatCurrency(pendingPayments),
      completedPayments: formatCurrency(completedPayments),
      recentTransactions: [] // Could be fetched from a transactions table
    };
  } catch (error) {
    console.error('Error fetching financial summary:', error);
    return {
      totalSpent: '₺0',
      activeCampaigns: '0',
      commission: '₺0',
      pendingPayments: '₺0',
      completedPayments: '₺0',
      recentTransactions: []
    };
  }
};

// Helper functions
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000)?.toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000)?.toFixed(1) + 'K';
  }
  return num?.toString();
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })?.format(amount);
};

const getPlatformIcon = (platformName) => {
  const name = platformName?.toLowerCase();
  if (name?.includes('instagram')) return 'Instagram';
  if (name?.includes('youtube')) return 'Youtube';
  if (name?.includes('tiktok')) return 'Music';
  if (name?.includes('twitter')) return 'Twitter';
  return 'Globe';
};